import { Button, Col, Input, Row, message, Spin, Modal } from "antd";
import FormLayout, { FormSection, FormItem } from "Components/form";
import React, { useEffect } from "react";
import FetchSelect from "Components/fetch-select";
import { Controller, useForm } from "react-hook-form";
import RealEstateSelect from "Components/realestate-select";
import Yup from "ValidationModels/localizedYup";
import MeasureList from "Components/form/measure-list";
import { clean } from "Utils/";
import { createComponentStore } from "easy-peasy";
import { DISPLAY_MODE } from "Constants";
import {
  SubUnitStoreModel,
  ACTION_TYPES,
} from "store/models/master-data/sub-unit";

import { yupResolver } from "@hookform/resolvers";

const FormValidationSchema = Yup.object({
  description: Yup.string().max(1000).required(),
  address: Yup.string().required(),
  subUnitType: Yup.object().nullable().required(),
  unit: Yup.object().nullable().required(),
});

const useSubUnitsStore = createComponentStore(SubUnitStoreModel);

export default function SubUnitForm({
  subUnit,
  onAfterSuccess,
  loading = false,
  mode = DISPLAY_MODE.READ_ONLY,
  ...props
}) {
  const [store, actions] = useSubUnitsStore();

  const form = useForm({
    mode: "onChange",
    resolver: yupResolver(FormValidationSchema),
    defaultValues: subUnit,
  });

  const { control, handleSubmit, errors, formState, reset } = form;

  useEffect(() => {
    if (subUnit) reset(subUnit);
  }, [subUnit, reset]);

  useEffect(() => {
    if (store.response)
      switch (store.process) {
        case ACTION_TYPES.CREATED_DATA:
          message.success("Alt Birim oluşturuldu.");
          if (onAfterSuccess) onAfterSuccess(store.response);
          break;
        case ACTION_TYPES.UPDATED_DATA:
          message.success("Alt Birim güncellendi.");
          if (onAfterSuccess) onAfterSuccess(store.response);
          break;
        default:
          break;
      }
  }, [store.process, store.response]);

  useEffect(() => {
    if (Object.keys(errors).length > 0)
      message.error("Alt birim için girdiğiniz değerleri kontrol ediniz.");
  }, [errors]);

  const formOnSubmit = async (form) => {
    clean(form);

    if (mode === DISPLAY_MODE.EDIT) {
      let payload = {
        ...subUnit,
        ...form,
        measures: form.measures?.map((measure) => {
          if (measure.id)
            return {
              ...subUnit.measures?.find((f) => f.id === measure.id),
              ...measure,
            };

          return measure;
        }),
      };

      actions.update(payload);
    }

    if (mode === DISPLAY_MODE.NEW) actions.create(form);
  };

  return (
    <Spin spinning={loading}>
      <form onSubmit={handleSubmit(formOnSubmit)} style={{ margin: "0 24px" }}>
        <FormLayout>
          <Col xs={24}>
            <FormSection title="Genel">
              <Row gutter={16}>
                {subUnit?.code ? (
                  <Col xs={24}>
                    <FormItem label="Kod" error={errors.code} required={true}>
                      <Controller
                        control={control}
                        name="code"
                        as={<Input readOnly={true} placeholder="Kod giriniz" />}
                      />
                    </FormItem>
                  </Col>
                ) : null}
                <Col xs={24}>
                  <FormItem
                    label="Açıklama"
                    error={errors.description}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="description"
                      as={
                        <Input.TextArea
                          rows={3}
                          placeholder="Açıklama yazınız"
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                        />
                      }
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    htmlFor="subUnitType"
                    required={true}
                    error={errors.subUnitType}
                    label="Alt Birim Türü"
                  >
                    <FetchSelect
                      api="/masterdata/subunittypes"
                      form={form}
                      field="subUnitType"
                      placeholder="Alt birim türü seçiniz"
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem
                    htmlFor="unit"
                    required={true}
                    error={errors.unit}
                    label="Birim"
                  >
                    <FetchSelect
                      api="/transactional/units/"
                      form={form}
                      field="unit"
                      placeholder="Birim seçiniz"
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem label="Blok No">
                    <Controller
                      control={control}
                      name="blockNo"
                      as={
                        <Input
                          placeholder="Blok no giriniz"
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                        />
                      }
                    />
                  </FormItem>
                </Col>
                <Col xs={24} md={24}>
                  <FormItem label="Taşınmaz">
                    <RealEstateSelect
                      form={form}
                      field={`realEstate`}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                    />
                  </FormItem>
                </Col>
              </Row>
            </FormSection>
            <FormSection title="Konum">
              <Row gutter={16}>
                <Col xs={24}>
                  <FormItem
                    label="Adres"
                    error={errors.address}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="address"
                      as={
                        <Input.TextArea
                          rows={3}
                          placeholder="Adres yazınız"
                          readOnly={mode === DISPLAY_MODE.READ_ONLY}
                        />
                      }
                    />
                  </FormItem>
                </Col>
              </Row>
            </FormSection>
            <FormSection title="Ölçüler">
              <MeasureList form={form} mode={mode} />
            </FormSection>

            {mode === DISPLAY_MODE.READ_ONLY ? null : (
              <Row
                justify="space-between"
                style={{
                  borderTop: "1px dashed rgb(166, 157, 152)",
                  position: "sticky",
                  bottom: 0,
                  padding: "16px 0",
                  backgroundColor: "#f2f2f2",
                }}
              >
                <Col>
                  <Button
                    loading={
                      store.process === ACTION_TYPES.CREATING_DATA ||
                      store.process === ACTION_TYPES.UPDATING_DATA
                    }
                    style={{ width: 120, marginRight: 8 }}
                    type="primary"
                    htmlType="submit"
                  >
                    Kaydet
                  </Button>
                  <Button
                    onClick={() => {
                      Modal.confirm({
                        title: "İptal",
                        content:
                          "Kaydetmediğiniz tüm değişiklikler, geri alınacaktır.",
                        okText: "Evet",
                        cancelText: "İptal",
                        onOk: () => {
                          reset();
                        },
                      });
                    }}
                  >
                    İptal
                  </Button>
                </Col>
              </Row>
            )}
          </Col>
        </FormLayout>
      </form>
    </Spin>
  );
}
